import { comoObjeto, leerBooleano, leerCampo, leerNumero, leerTexto } from '../../utils/leerCampo';

/*
 * Lectores del `Datos` de los avisos de conciliación y reasignación (PES-411, PES-412, PES-759). Forma por
 * tipo: `docs/BUZON.md` del repo `RfacilBack_SM_BC`. `Datos` es JSON libre de otro sistema, así que:
 * - puede llegar como objeto o como texto JSON;
 * - las claves de FACTURA_CANCELADA_SAT, FACTURA_MATERIALIDAD_RELACIONADA y FACTURA_NO_RECONOCIDA_CANCELADA viajan en camelCase (la API
 *   serializa un objeto anónimo y el SSO guarda `Datos` tal cual), pero se leen en las dos formas;
 * - nada se asume: un campo ausente o de otro tipo es `null`, y los lectores nunca lanzan.
 */

/** `Datos` como objeto: acepta el objeto ya parseado o su texto JSON; `null` si no es ninguno. */
export const normalizarDatos = (datos: unknown): Record<string, unknown> | null => {
  if (typeof datos === 'string') {
    try {
      return comoObjeto(JSON.parse(datos));
    } catch {
      return null;
    }
  }
  return comoObjeto(datos);
};

/** Cómo llegó la materialidad a la factura padre. */
export type EscenarioMaterialidad = 'Relacionado' | 'Sustitucion';

const leerEscenario = (datos: unknown): EscenarioMaterialidad | null => {
  const crudo = (leerTexto(datos, 'Escenario') ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim()
    .toLowerCase();
  if (crudo === 'relacionado') {
    return 'Relacionado';
  }
  return crudo === 'sustitucion' ? 'Sustitucion' : null;
};

/** `Datos` de FACTURA_MATERIALIDAD_RELACIONADA. */
export interface DatosMaterialidadRelacionada {
  /** De dónde viene el aviso (hoy siempre `Conciliacion`). */
  origen: string | null;
  escenario: EscenarioMaterialidad | null;
  idFacturaPadre: number | null;
  /** `null` en `Relacionado`; el id de la cancelada en `Sustitucion`. */
  idFacturaRelacionada: number | null;
  idDocumento: number | null;
  /** `false` si no hubo documento que adjuntar. Si no viene, se deduce de `idDocumento`. */
  conDocumento: boolean;
}

/** Lee el `Datos` de FACTURA_MATERIALIDAD_RELACIONADA; `null` si `Datos` no es un objeto. */
export const leerDatosMaterialidadRelacionada = (datos: unknown): DatosMaterialidadRelacionada | null => {
  const d = normalizarDatos(datos);
  if (!d) {
    return null;
  }
  const idDocumento = leerNumero(d, 'IdDocumento');
  return {
    origen: leerTexto(d, 'Origen'),
    escenario: leerEscenario(d),
    idFacturaPadre: leerNumero(d, 'IdFacturaPadre'),
    idFacturaRelacionada: leerNumero(d, 'IdFacturaRelacionada'),
    idDocumento,
    conDocumento: leerBooleano(d, 'ConDocumento') ?? idDocumento !== null,
  };
};

/** Un documento de materialidad que la factura cancelada ya tenía. */
export interface DocumentoMaterialidadAviso {
  idDocumento: number | null;
  /** `null` si el SSO lo enmascaró (traía un UUID o un RFC). */
  tipo: string | null;
  /** `null` si el SSO lo enmascaró (traía un UUID o un RFC). */
  nombre: string | null;
}

/** `Datos` de FACTURA_CANCELADA_SAT. */
export interface DatosFacturaCancelada {
  origen: string | null;
  escenario: string | null;
  idFactura: number | null;
  /**
   * Documentos de materialidad (máximo 20). `null` = las claves no vinieron: la API no distingue "no tenía"
   * de "no se pudo leer", así que NO debe leerse como "sin materialidad".
   */
  materialidad: DocumentoMaterialidadAviso[] | null;
  /** Total real de documentos (puede ser mayor que la lista); `null` si no vino y no hay lista. */
  totalMaterialidad: number | null;
}

/** Lee el `Datos` de FACTURA_CANCELADA_SAT; `null` si `Datos` no es un objeto. */
export const leerDatosFacturaCancelada = (datos: unknown): DatosFacturaCancelada | null => {
  const d = normalizarDatos(datos);
  if (!d) {
    return null;
  }
  const crudo = leerCampo(d, 'Materialidad');
  const lista = Array.isArray(crudo) ? crudo : null;
  const materialidad = lista
    ? lista.map((item) => ({
        idDocumento: leerNumero(item, 'IdDocumento'),
        tipo: leerTexto(item, 'Tipo'),
        nombre: leerTexto(item, 'Nombre'),
      }))
    : null;
  return {
    origen: leerTexto(d, 'Origen'),
    escenario: leerTexto(d, 'Escenario'),
    idFactura: leerNumero(d, 'IdFactura'),
    materialidad,
    totalMaterialidad: leerNumero(d, 'TotalMaterialidad') ?? (materialidad ? materialidad.length : null),
  };
};

/** Cómo estaba marcada una factura «no reconocida» (PES-397) cuando el SAT la canceló. */
export type EstadoNoReconocida = 'Duplicada' | 'FacturadaPorError';

/** `Datos` de FACTURA_NO_RECONOCIDA_CANCELADA (sin UUID ni RFC; el aviso no enlaza a ninguna factura). */
export interface DatosNoReconocidaCancelada {
  origen: string | null;
  escenario: string | null;
  /** Id de la fila de la conciliación (`IdEntidad` de la notificación). */
  idFacturaSat: number | null;
  /** `null` si no vino o no es uno de los dos estados con los que se marca. */
  estadoExcepcion: EstadoNoReconocida | null;
}

/** Lee el `Datos` de FACTURA_NO_RECONOCIDA_CANCELADA; `null` si `Datos` no es un objeto. */
export const leerDatosNoReconocidaCancelada = (datos: unknown): DatosNoReconocidaCancelada | null => {
  const d = normalizarDatos(datos);
  if (!d) {
    return null;
  }
  const estado = leerTexto(d, 'EstadoExcepcion');
  return {
    origen: leerTexto(d, 'Origen'),
    escenario: leerTexto(d, 'Escenario'),
    idFacturaSat: leerNumero(d, 'IdFacturaSat'),
    estadoExcepcion: estado === 'Duplicada' || estado === 'FacturadaPorError' ? estado : null,
  };
};

/** `Datos` de SOLICITUD_REASIGNACION (el motivo viaja en `Motivo` de la notificación, no aquí). */
export interface DatosSolicitudReasignacion {
  idFactura: number | null;
  total: number | null;
}

export const leerDatosSolicitudReasignacion = (datos: unknown): DatosSolicitudReasignacion | null => {
  const d = normalizarDatos(datos);
  return d ? { idFactura: leerNumero(d, 'IdFactura'), total: leerNumero(d, 'Total') } : null;
};

/** `Datos` de FACTURA_REASIGNADA_PORTAL (`{ Total }`) y de SOLICITUD_REASIGNACION_RECHAZADA (`{ IdSolicitud, Total }`). */
export interface DatosTotalFactura {
  idSolicitud: number | null;
  total: number | null;
}

export const leerDatosTotalFactura = (datos: unknown): DatosTotalFactura | null => {
  const d = normalizarDatos(datos);
  return d ? { idSolicitud: leerNumero(d, 'IdSolicitud'), total: leerNumero(d, 'Total') } : null;
};
